import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, unref as _unref, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "formulario-modelo" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { class: "header-flex" }
const _hoisted_4 = {
  key: 0,
  class: "ctx-modelo"
}
const _hoisted_5 = { class: "filtro-botones" }
const _hoisted_6 = {
  key: 0,
  class: "filtro-select"
}
const _hoisted_7 = { for: "componente-filtro" }
const _hoisted_8 = ["value"]
const _hoisted_9 = { class: "marco" }
const _hoisted_10 = {
  key: 0,
  class: "mensaje-vacio"
}
const _hoisted_11 = { class: "botones" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { class: "botones" }

import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useComponentes } from '@/store/GestionComponentes'
import { useGestionVersiones } from '@/store/GestionVersiones'
import useModelo from '@/store/GestionModelo'
import type Componente from '@/interfaces/Componente'
import MensajeConfirmacion from '@/components/MensajeConfirmacion.vue'
import ModificarComponentes from '@/components/ModificarComponentes.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import FormularioComponente from '@/components/FormularioComponente.vue'
import { limpiarNiveles } from '@/utils/nivelesHelper'

interface ComponenteAplanado {
  ref: Componente
  nivel: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AgregarComponentesView',
  setup(__props) {

/**
 * Construcción del árbol de estructura del modelo.
 *
 * Solo operativa con el MODELO en estado `edicion`. Los cambios afectan a TODAS
 * las versiones, porque la estructura es del modelo y se comparte.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/version/:id_version/componentes/agregar` (AgregarComponente) — permiso `componentes.crear`
 *   `/modelo/:id_modelo/componentes` (ComponentesModelo) — permiso `componentes.ver`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

const route = useRoute()
const router = useRouter()
const store = useComponentes()
const versionStore = useGestionVersiones()
const modeloStore = useModelo()
const idVersion = Number(route.params.id_version ?? NaN)
const idModeloDirecto = Number(route.params.id_modelo ?? NaN)
const sinVersion = Number.isFinite(idModeloDirecto) && idModeloDirecto > 0
const loading = ref(false)
const feedback = ref('')
const feedbackEsPositivo = ref(false)
const mostrarConfirmar = ref(false)

// Filtro de componentes
const tipoFiltro = ref<'todos' | 'por-componente'>('todos')
const componenteNivel1Filtro = ref<number | null>(null)

const componentesNivel1 = computed(() => {
  const hijos = store.componentes[0]?.children ?? []
  return [...hijos].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
})

const componentesFiltrados = computed(() => {
  const hijos = store.componentes[0]?.children ?? []
  if (tipoFiltro.value === 'por-componente' && componenteNivel1Filtro.value != null) {
    const comp = hijos.find(c => c.id_componente === componenteNivel1Filtro.value)
    return comp ? [comp] : []
  }
  return hijos
})

const nivelesArray = ref<string[]>([])
const idComponenteRaiz = ref<number | null>(null)
const idModelo = ref<number | null>(null)
const nombreModelo = ref('')
const loadingInicial = ref(true)
const loadingAccion = ref(false)  
const nombreNivelSiguiente = ref('')  
const componentesAplanados = ref<ComponenteAplanado[]>([])
const mostrarConfirmarEliminar = ref(false)
const compSeleccionado = ref<Componente | null>(null)

function nombreNivelDeComponente(comp: Componente): string {
  const aplanado = componentesAplanados.value.find(c => c.ref.id_componente === comp.id_componente)
  if (!aplanado) return 'Elemento'
  return nivelesArray.value[aplanado.nivel - 1] ?? 'Elemento'
}

function aplanarComponentes(arbol: Componente[], nivel = 0): ComponenteAplanado[] {
  const resultado: ComponenteAplanado[] = []
  for (const comp of arbol) {
    if (nivel > 0) {
      resultado.push({ ref: comp, nivel })
    }
    if ((comp.children?.length ?? 0) > 0) {
      resultado.push(...aplanarComponentes(comp.children!, nivel + 1))
    }
  }
  return resultado
}

function obtenerPadreRef(padreId: number | null | undefined): Componente | null {
  if (padreId == null) return null
  const padreAplanado = componentesAplanados.value.find(c => c.ref.id_componente === padreId)
  return padreAplanado?.ref ?? null
}

function obtenerNumeracionJerarquica(componente: Componente, nivel: number): string {
  const aplanado = componentesAplanados.value.find(c => c.ref.id_componente === componente.id_componente)
  if (!aplanado) return ''
  
  const numeros: number[] = []
  let actual: ComponenteAplanado | null = aplanado
  
  while (actual && actual.nivel > 0) {
    const padre = obtenerPadreRef(actual.ref.padre_id)
    if (!padre) {
      const hermanos = componentesAplanados.value.filter(c => c.nivel === 1)
      const hermanosSorted = hermanos.sort((a, b) => 
        (a.ref.ordinal ?? '').localeCompare(b.ref.ordinal ?? '', undefined, { numeric: true })
      )
      const posicion = hermanosSorted.findIndex(h => h.ref.id_componente === actual!.ref.id_componente)
      numeros.unshift(posicion + 1)
      break
    } else {
      const hermanos = padre.children ?? []
      const hermanosSorted = [...hermanos].sort((a, b) => 
        (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
      )
      const posicion = hermanosSorted.findIndex(h => h.id_componente === actual!.ref.id_componente)
      numeros.unshift(posicion + 1)
      
      const actualAplanado = componentesAplanados.value.find(c => c.ref.id_componente === padre.id_componente)
      actual = actualAplanado ?? null
    }
  }
  const numero = numeros.join('.')
  if (!numero) return ''
  // Usar el nivel real del árbol (aplanado.nivel es 1-based) para obtener el nombre correcto
  const nombreNivel = nivelesArray.value[aplanado.nivel - 1] ?? `Nivel ${aplanado.nivel}`
  return `${nombreNivel} ${numero}`
}


onMounted(async () => {
  if (!sinVersion && (!Number.isFinite(idVersion) || idVersion <= 0)) {
    feedback.value = 'ID de versión inválido.'
    feedbackEsPositivo.value = false
    return
  }

  loadingInicial.value = true
  try {
    if (sinVersion) {
      // Acceso directo por modelo (sin versión)
      const modelo = await modeloStore.getModeloById(idModeloDirecto)
      if (modelo?.niveles) {
        nivelesArray.value = limpiarNiveles(modelo.niveles)
      }
      nombreModelo.value = modelo?.nombre ?? ''
      idModelo.value = idModeloDirecto
    } else {
      const version = await versionStore.getVersionById(idVersion)
      if (version?.id_modelo) {
        const modelo = await modeloStore.getModeloById(version.id_modelo)
        if (modelo?.niveles) {
          nivelesArray.value = limpiarNiveles(modelo.niveles)
        }
        nombreModelo.value = modelo?.nombre ?? ''
      }
      // C-01: componentes pertenecen al modelo; cargamos por id_modelo
      idModelo.value = version?.id_modelo ?? null
    }
    if (!idModelo.value) {
      feedback.value = 'No se pudo obtener el modelo.'
      feedbackEsPositivo.value = false
      return
    }
    const raiz = await store.cargarRaizPorModelo(idModelo.value)
    if (!raiz) {
      feedback.value = 'No se encontró el componente raíz.'
      feedbackEsPositivo.value = false
      return
    }

    idComponenteRaiz.value = raiz.id_componente ?? null

    if (idComponenteRaiz.value != null) {
      const ok = await store.cargarArbolDesdeRaiz(idComponenteRaiz.value)
      if (!ok) {
        feedback.value = 'No fue posible cargar la versión y sus componentes.'
        feedbackEsPositivo.value = false
      } else {
        componentesAplanados.value = aplanarComponentes(store.componentes)
      }
    } else {
      feedback.value = 'No se encontró el ID de la raíz para cargar el árbol.'
      feedbackEsPositivo.value = false
    }
  } catch (e: any) {
    feedback.value = e?.message || 'Error al cargar la versión.'
    feedbackEsPositivo.value = false
  } finally {
    loadingInicial.value = false
  }
})

// Actualizar array aplanado cuando cambian los componentes
watch(
  () => store.componentes,
  () => {
    componentesAplanados.value = aplanarComponentes(store.componentes)
  },
  { deep: true }
)

// Formulario nivel 1
const mostrarFormNivel1 = ref(false)

function cancelarNivel1() {
  mostrarFormNivel1.value = false
}

async function onAceptarNivel1(payload: { nombre: string; descripcion: string; enfoque?: string; grupo_variante?: string | null }) {
  if (!idComponenteRaiz.value) return
  try {
    loading.value = true

    const datos = {
      nombre: payload.nombre,
      peso: 0,
      descripcion: payload.descripcion || null,
      enfoque: payload.enfoque ?? 'general',
      grupo_variante: payload.grupo_variante ?? null
    }

    const result = await store.agregarHijo(idComponenteRaiz.value, datos as any)
    if (!result.data) {
      feedback.value = result.error ?? `No fue posible crear el ${(nivelesArray.value[0] ?? 'elemento').toLowerCase()}.`
      feedbackEsPositivo.value = false
      cancelarNivel1()
      return
    }

    cancelarNivel1()
    feedback.value = `${nivelesArray.value[0] ?? 'Elemento'} agregado.`
    feedbackEsPositivo.value = true
  } catch (e: any) {
    feedback.value = e?.message || `Error al agregar ${(nivelesArray.value[0] ?? 'elemento').toLowerCase()}.`
    feedbackEsPositivo.value = false
    cancelarNivel1()
  } finally {
    loading.value = false
  }
}

// Formulario hijos
const mostrarFormHijo = ref(false)
const padreSeleccionado = ref<Componente | null>(null)

function cancelarHijo() {
  padreSeleccionado.value = null
  mostrarFormHijo.value = false
}

async function onAceptarHijo(payload: { nombre: string; descripcion: string; enfoque?: string; grupo_variante?: string | null }) {
  if (!padreSeleccionado.value) return
  try {
    loading.value = true

    const datos = {
      nombre: payload.nombre,
      peso: 0,
      descripcion: payload.descripcion || null,
      enfoque: payload.enfoque ?? 'general',
      grupo_variante: payload.grupo_variante ?? null
    }

    const result = await store.agregarHijo(padreSeleccionado.value.id_componente!, datos as any)
    if (!result.data) {
      feedback.value = result.error ?? `No fue posible crear el ${nombreNivelSiguiente.value.toLowerCase()}.`
      feedbackEsPositivo.value = false
      cancelarHijo()
      return
    }

    cancelarHijo()
    feedback.value = `${nombreNivelSiguiente.value} agregado.`
    feedbackEsPositivo.value = true
  } catch (e: any) {
    feedback.value = e?.message || `Error al agregar ${nombreNivelSiguiente.value.toLowerCase()}.`
    feedbackEsPositivo.value = false
    cancelarHijo() 
  } finally {
    loading.value = false
  }
}

// Formulario modificar
const mostrarFormModificar = ref(false)
const componenteEditando = ref<Componente | null>(null)
const nombreModificar = ref('')
const descripcionModificar = ref('')
const enfoqueModificar = ref<string | null>('general')
const grupoModificar = ref<string | null>('')

function cancelarModificar() {
  nombreModificar.value = ''
  descripcionModificar.value = ''
  enfoqueModificar.value = 'general'
  grupoModificar.value = ''
  componenteEditando.value = null
  mostrarFormModificar.value = false
}

// Eventos desde ModificarComponentes
function onModificarComponente(comp: Componente) {
  componenteEditando.value = comp
  nombreModificar.value = comp.nombre
  descripcionModificar.value = comp.descripcion ?? ''
  enfoqueModificar.value = comp.enfoque ?? 'general'
  grupoModificar.value = comp.grupo_variante ?? ''
  mostrarFormModificar.value = true
}

async function onAceptarModificar(payload: { nombre: string; descripcion: string; enfoque?: string; grupo_variante?: string | null }) {
  if (!componenteEditando.value) return
  try {
    loading.value = true

    const compActualizado: Componente = {
      ...componenteEditando.value,
      nombre: payload.nombre,
      descripcion: payload.descripcion || null,
      enfoque: payload.enfoque ?? 'general',
      grupo_variante: payload.grupo_variante ?? null
    }

    const pesoAnterior = compActualizado.peso
    const nivelNombre = nombreNivelDeComponente(componenteEditando.value!)
    const actualizado = await store.actualizar(compActualizado)
    if (!actualizado) {
      feedback.value = `Error al actualizar el ${nivelNombre.toLowerCase()}.`
      feedbackEsPositivo.value = false
      cancelarModificar()
      return
    }

    if (pesoAnterior !== actualizado.peso) {
      const padreId = actualizado.padre_id ?? actualizado.id_componente
      if (typeof padreId === 'number') {
        await store.redistribuirPesosEntreHermanos(
          padreId,
          actualizado.id_componente!,
          actualizado.peso ?? 0
        )
      }
    }

    cancelarModificar()
    feedback.value = `${nivelNombre} modificado.`
    feedbackEsPositivo.value = true
  } catch (e: any) {
    feedback.value = e?.message || 'Error al modificar el elemento.'
    feedbackEsPositivo.value = false
    cancelarModificar()
  } finally {
    loading.value = false
  }
}

function onEliminarComponente(comp: Componente) {
  compSeleccionado.value = comp
  mostrarConfirmarEliminar.value = true
}

async function ejecutarEliminar() {
  if (!compSeleccionado.value) return
  const nivelNombre = nombreNivelDeComponente(compSeleccionado.value)
  loadingAccion.value = true
  try {
    const ok = await store.eliminar(compSeleccionado.value.id_componente!)
    if (ok) {
      feedback.value = `${nivelNombre} eliminado.`
      feedbackEsPositivo.value = true
    } else {
      feedback.value = `No se pudo eliminar el ${nivelNombre.toLowerCase()}.`
      feedbackEsPositivo.value = false
    }
  } catch (e: any) {
    feedback.value = e?.message || `Error al eliminar el ${nivelNombre.toLowerCase()}.`
    feedbackEsPositivo.value = false
  } finally {
    loadingAccion.value = false
    mostrarConfirmarEliminar.value = false
    compSeleccionado.value = null
  }
}

function onAgregarHijo(payload: { padre: Componente; nivel: number }) {
  padreSeleccionado.value = payload.padre
  nombreNivelSiguiente.value = nivelesArray.value[payload.nivel + 1] ?? 'Componente'
  mostrarFormHijo.value = true
}

// Reordenar componente entre sus hermanos
async function reordenarComponente(componente: Componente, nuevaPosicion: number): Promise<void> {
  if (!componente.id_componente) return
  const nivelNombre = nombreNivelDeComponente(componente)

  if (componente.padre_id == null) {
    feedback.value = `El ${nivelNombre.toLowerCase()} raíz no puede reordenarse.`
    feedbackEsPositivo.value = false
    return
  }

  const hermanos = componentesAplanados.value.filter(c => c.ref.padre_id === componente.padre_id)

  if (hermanos.length < 2) {
    feedback.value = `El ${nivelNombre.toLowerCase()} no tiene elementos del mismo nivel con los que intercambiar posición.`
    feedbackEsPositivo.value = false
    return
  }

  if (nuevaPosicion < 1 || nuevaPosicion > hermanos.length) {
    feedback.value = `Posición inválida. Rango permitido: 1–${hermanos.length}.`
    feedbackEsPositivo.value = false
    return
  }

  loadingAccion.value = true
  try {
    const result = await store.reordenar(componente.id_componente, nuevaPosicion)
    if (result.ok) {
      feedback.value = `${nivelNombre} reordenado.`
      feedbackEsPositivo.value = true
      if (idComponenteRaiz.value != null) {
        await store.cargarArbolDesdeRaiz(idComponenteRaiz.value)
        componentesAplanados.value = aplanarComponentes(store.componentes)
      }
    } else {
      feedback.value = result.message ?? `No se pudo reordenar el ${nivelNombre.toLowerCase()}.`
      feedbackEsPositivo.value = false
    }
  } catch (e: any) {
    feedback.value = e?.message ?? `Error al reordenar el ${nivelNombre.toLowerCase()}.`
    feedbackEsPositivo.value = false
  } finally {
    loadingAccion.value = false
  }
}

function onReordenarComponente(payload: { dragged: Componente; target: Componente; side: 'before' | 'after' }) {
  const { dragged, target, side } = payload
  const hermanos = componentesAplanados.value
    .filter(c => c.ref.padre_id === dragged.padre_id)
    .sort((a, b) => (a.ref.ordinal ?? '').localeCompare(b.ref.ordinal ?? '', undefined, { numeric: true }))
  const reduced = hermanos.filter(c => c.ref.id_componente !== dragged.id_componente)
  const targetIdx = reduced.findIndex(c => c.ref.id_componente === target.id_componente)
  if (targetIdx === -1) return
  const insertIdx = side === 'before' ? targetIdx : targetIdx + 1
  reordenarComponente(dragged, insertIdx + 1)
}

function salir() {
  router.push({ name: 'ListaVersiones' })
}

// Completar modelo
async function CompletarModelo() {
  if (loadingAccion.value) return
  feedback.value = ''
  if (!idModelo.value) {
    feedback.value = 'No se pudo identificar el modelo.'
    feedbackEsPositivo.value = false
    return
  }

  loadingAccion.value = true
  try {
    if (sinVersion) {
      await versionStore.getVersiones()
      const tieneVersiones = versionStore.versiones.some(v => v.id_modelo === idModelo.value)
      if (!tieneVersiones) {
        const versionCreada = await versionStore.crearVersion({
          id_modelo: idModelo.value,
          nombre: modeloStore.modeloActual?.nombre ?? '',
          estado: 'edicion',
          fecha_creacion: new Date().toISOString().split('T')[0],
          fecha_aprobacion: null,
          ambito: null,
          id_programa: null
        })
        if (!versionCreada) {
          feedback.value = 'No se pudo crear la versión del modelo.'
          feedbackEsPositivo.value = false
          return
        }
      }
    }

    feedback.value = 'Modelo Finalizado'
    feedbackEsPositivo.value = true
    setTimeout(() => router.push({ name: 'ListaVersiones' }), 800)
  } catch (err: any) {
    console.error('Error al aprobar modelo:', err)
    feedback.value = err?.message || 'Error al completar el modelo.'
    feedbackEsPositivo.value = false
  } finally {
    loadingAccion.value = false
  }
}

function irAGestionPesos() {
  if (!idModelo.value) {
    feedback.value = 'No se pudo identificar el modelo.'
    feedbackEsPositivo.value = false
    return
  }
  router.push({ name: 'Pesos', params: { id_modelo: idModelo.value } })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(LoadingOverlay, {
      visible: loadingInicial.value,
      message: "Cargando componentes..."
    }, null, 8, ["visible"]),
    _createVNode(LoadingOverlay, {
      visible: loadingAccion.value,
      message: "Procesando acción..."
    }, null, 8, ["visible"]),
    (!loadingInicial.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", null, [
              _cache[8] || (_cache[8] = _createElementVNode("h1", null, "Agregar Elementos", -1)),
              (nombreModelo.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(nombreModelo.value), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("button", {
              class: "btn guardar",
              onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (mostrarConfirmar.value = true), ["prevent"]))
            }, " Finalizar ")
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("button", {
              class: _normalizeClass(['btn-filtro', tipoFiltro.value === 'todos' ? 'activo' : '']),
              onClick: _cache[1] || (_cache[1] = ($event: any) => {tipoFiltro.value = 'todos'; componenteNivel1Filtro.value = null})
            }, " Mostrar todo el modelo ", 2),
            _createElementVNode("button", {
              class: _normalizeClass(['btn-filtro', tipoFiltro.value === 'por-componente' ? 'activo' : '']),
              onClick: _cache[2] || (_cache[2] = ($event: any) => (tipoFiltro.value = 'por-componente'))
            }, " Por " + _toDisplayString(nivelesArray.value[0] ?? 'Componente'), 3)
          ]),
          (tipoFiltro.value === 'por-componente')
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createElementVNode("label", _hoisted_7, "Selecciona un " + _toDisplayString(nivelesArray.value[0]?.toLowerCase() ?? 'componente') + ":", 1),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((componenteNivel1Filtro).value = $event)),
                  id: "componente-filtro",
                  class: "form-control"
                }, [
                  _cache[9] || (_cache[9] = _createElementVNode("option", { value: null }, "-- Selecciona --", -1)),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(componentesNivel1.value, (comp) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: comp.id_componente,
                      value: comp.id_componente
                    }, _toDisplayString(comp.ordinal) + " - " + _toDisplayString(comp.nombre), 9, _hoisted_8))
                  }), 128))
                ], 512), [
                  [
                    _vModelSelect,
                    componenteNivel1Filtro.value,
                    void 0,
                    { number: true }
                  ]
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_9, [
            ((_unref(store).componentes[0]?.children ?? []).length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, _cache[10] || (_cache[10] = [
                  _createElementVNode("p", null, "El Modelo no tiene elementos aún agregados", -1)
                ])))
              : (_openBlock(), _createBlock(ModificarComponentes, {
                  key: 1,
                  componentes: componentesFiltrados.value,
                  idComponenteRaiz: idComponenteRaiz.value,
                  componenteEditandoId: componenteEditando.value?.id_componente ?? null,
                  niveles: nivelesArray.value,
                  obtenerNumeracion: obtenerNumeracionJerarquica,
                  onModificar: onModificarComponente,
                  onEliminar: onEliminarComponente,
                  onAgregarHijo: onAgregarHijo,
                  onReordenar: onReordenarComponente
                }, null, 8, ["componentes", "idComponenteRaiz", "componenteEditandoId", "niveles"])),
            _createVNode(FormularioComponente, {
              visible: mostrarFormNivel1.value,
              modo: "agregarNivel1",
              loading: loading.value,
              nombreNivelSiguiente: nivelesArray.value[0] ?? 'Componente',
              onAceptar: onAceptarNivel1,
              onCancelar: cancelarNivel1
            }, null, 8, ["visible", "loading", "nombreNivelSiguiente"]),
            _createVNode(FormularioComponente, {
              visible: mostrarFormHijo.value,
              modo: "agregarHijo",
              loading: loading.value,
              nombrePadre: padreSeleccionado.value?.nombre ?? '',
              nombreNivelSiguiente: nombreNivelSiguiente.value,
              onAceptar: onAceptarHijo,
              onCancelar: cancelarHijo
            }, null, 8, ["visible", "loading", "nombrePadre", "nombreNivelSiguiente"]),
            _createVNode(FormularioComponente, {
              visible: mostrarFormModificar.value,
              modo: "modificar",
              loading: loading.value,
              nombreComponente: componenteEditando.value?.nombre ?? '',
              nombreInicial: nombreModificar.value,
              descripcionInicial: descripcionModificar.value,
              enfoqueInicial: enfoqueModificar.value,
              grupoInicial: grupoModificar.value,
              onAceptar: onAceptarModificar,
              onCancelar: cancelarModificar
            }, null, 8, ["visible", "loading", "nombreComponente", "nombreInicial", "descripcionInicial", "enfoqueInicial", "grupoInicial"])
          ]),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("button", {
              class: "btn guardar",
              onClick: _cache[4] || (_cache[4] = ($event: any) => (mostrarFormNivel1.value = true)),
              disabled: loading.value || !idComponenteRaiz.value
            }, " Agregar " + _toDisplayString(nivelesArray.value[0] ?? 'Componente'), 9, _hoisted_12),
            _createElementVNode("button", {
              class: "btn guardar",
              onClick: irAGestionPesos
            }, " Ajustar pesos ")
          ]),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("button", {
              class: "btn salir",
              onClick: salir
            }, "Salir"),
            _createVNode(MensajeConfirmacion, {
              visible: mostrarConfirmar.value,
              title: "Confirmar acción",
              message: "¿Desea finalizar los ajustes del modelo?",
              confirmText: "Aceptar",
              cancelText: "Cancelar",
              confirmLoading: loadingAccion.value,
              onConfirm: CompletarModelo,
              onCancel: _cache[5] || (_cache[5] = ($event: any) => (mostrarConfirmar.value = false))
            }, null, 8, ["visible", "confirmLoading"])
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(MensajeFlotante, {
      visible: !!feedback.value,
      mensaje: feedback.value,
      tipo: feedbackEsPositivo.value ? 'positivo' : 'error',
      onCerrar: _cache[6] || (_cache[6] = ($event: any) => (feedback.value = ''))
    }, null, 8, ["visible", "mensaje", "tipo"]),
    _createVNode(MensajeConfirmacion, {
      visible: mostrarConfirmarEliminar.value,
      title: "Confirmar eliminación",
      message: `¿Eliminar ${compSeleccionado.value?.nombre}?`,
      confirmText: "Aceptar",
      cancelText: "Cancelar",
      confirmLoading: loadingAccion.value,
      onConfirm: ejecutarEliminar,
      onCancel: _cache[7] || (_cache[7] = ($event: any) => (mostrarConfirmarEliminar.value = false))
    }, null, 8, ["visible", "message", "confirmLoading"])
  ]))
}
}

})